// # EverFi's Content Partner SDK.
//
// Dependencies: jquery/backbone/underscore
//
//
// This file provides the top level namespace `EF` for all of our models,
// collections and controllers to be attached to and for the main methods of
// the API to live.
//
//
//
// Decorate EF namespace with our stuff.

window.EF = window.EF || {};
if (typeof module !== 'undefined') {
  module.exports = window.EF;
}

if (typeof Chaplin === 'undefined') {
  Chaplin = Backbone;
}

(function (EF, undefined) {
  _.extend(EF, Backbone.Events, {
    version: '0.5.8',

    // ### EF.init
    //
    // the init function sets up the API with the information it needs to operate.
    // this method takes 3 arguments:
    //   `EF.Models.Enrollment` Object
    //   `EF.Models.Module` Object
    //   `options` object. Includes a 'stage' key, which will be passed along to
    //      the page objects when they are initialized and signifies the location
    //      of the HTML on the page. Options can also have a 'apiOnly' key which
    //      should be a boolean value and will signify only using the
    //      SDK in api only mode, which doesn't play pages for you.
    //
    options: { local: true },
    init: function (groupsJSON, modulesJSON, modulesContentJson, enrollment_hash, options, config) {
      var self = this;
      // ContentProvider requires CourseSettings to be initialized to instantiate
      // which happens before this 'init' call in ef_sdk
      self.ContentProvider = require('services/content-provider');
      var defaults = {
        apiOnly: false,
        local: true,
        autoNextActivity: false,
        loadActivityProgresses: false,
        sandboxMode: false,
        pmMode: false,
        roboLog: false,
        saveLater: false,
        scriptFolder: 'scripts',
        templateFolder: 'templates',
        cssFolder: 'css',
      };
      this.urlHelper = this.urlHelper || new window.UrlHelper();
      this.groupsJSON = groupsJSON;
      this.modulesJSON = modulesJSON;
      this.config = this.urlHelper.buildConfig(config);
      if (config.accessToken) {
        Backbone.ajax = function () {
          if (!arguments[0].headers) {
            arguments[0].headers = { Authorization: `Bearer ${config.accessToken}` };
          } else if (!arguments[0].headers.Authorization) {
            arguments[0].headers.Authorization = `Bearer ${config.accessToken}`;
          }
          if (arguments[0].xhrFields === undefined || arguments[0].xhrFields.withCredentials === undefined) {
            arguments[0].xhrFields = arguments[0].xhrFields || {};
            arguments[0].xhrFields.withCredentials = true;
          }
          if (config.portalQueryString) {
            arguments[0].url = `${arguments[0].url}${arguments[0].url.includes('?') ? '&' : '?'}${
              config.portalQueryString
            }`;
          }
          return Backbone.$.ajax.apply(Backbone.$, arguments);
        };
      }
      this.options = _.defaults(this.urlHelper.buildOptions(options), defaults);
      this.enrollment = this.urlHelper.buildEnrollment(enrollment_hash);
      this.customizations = new EF.Collections.CustomizationsCollection([], { enrollment: this.enrollment });
      this.resources = new EF.Collections.ResourcesCollection([], { enrollment: this.enrollment });
      this.actionPlanTopics = new EF.Collections.ActionPlanTopicsCollection([], {
        enrollment: this.enrollment,
        actionPlanItemResponses: new EF.Collections.ActionPlanItemResponsesCollection([], {
          enrollment: this.enrollment,
        }),
      });
      if (this.options.sandboxMode) {
        console.warn('EF.options.sandboxMode is deprecated; slated for removal in 4.0.0');
        this.sandboxHelper = this._getSandboxHelperObj();
      }
      this.stage = this.config.get('stage');
      this.language = 'en';
      this.program = new EF.Models.Program([], { enrollment: this.enrollment });

      this.progressQueue = new EF.Models.ProgressQueue();

      if (this.options.apiOnly) {
        this.pageLoader = this._defaultPageLoader;
      }
      _.bindAll(this, 'clearLocalProgress', '_defaultPageLoader');
      // load here?
      EF.ContentProvider.ResourceService.loadResources().then(() => {
        this.setGroupsByJSON(this.groupsJSON);
        this.setModulesByJSON(this.modulesJSON, modulesContentJson, function () {
          self.attachCompletionEvents();

          var loads = self.ContentProvider.InitializationService.load();

          if (self.options.evaluations) {
            var evaluations = $.map(self.options.evaluations, function (value, index) {
              return [value];
            });
            EF.responseSet = new EF.Collections.ResponseSetCollection(evaluations);
          }

          if (self.options.loadActivityProgresses) {
            EF.activityProgresses = new EF.Collections.ActivityProgressesCollection();
            if (!self.options.local) {
              loads.push(EF.activityProgresses.fetch());
            }
          }

          if (self.options.local) {
            EF.modules.each(m => m.fetch());

            let skipping = window.LocalStorageWrapper.getItem('pageSkipping');
            EF.options.pageSkipping = skipping === undefined || skipping === null ? true : skipping === 'true';
          }
          $.when.apply(this, loads).done(function () {
            if (self.options.loadActivityProgresses) {
              EF.modules.syncActivityAndPageProgress(EF.activityProgresses);
            }
            EF.trigger('ready');
            EF.modules.loaded = true;
            if (EF.router) {
              Backbone.history.start();
              EF.trigger('routerReady');
            }
          });
        });
      });
    },

    Models: {
      Assessments: {},
      Evaluations: {},
    },

    Controllers: {},

    Collections: {
      Assessments: {},
    },

    //### registeredPages

    // registeredPages is where pages following the perscribed method of formatting
    // the page javascripts have their objects stored for retrieval.
    registeredPages: {},

    attachCompletionEvents: function () {
      this.on('contentPage:complete', function (contentPage) {
        //do nothing
      });

      this.on('contentPage:loaded', function (contentPage) {
        contentPage.trigger('loaded');
      });

      this.on('module:progress:set', function (module) {
        //do nothing
      });

      this.on('page:progress:set', function (contentPage) {
        contentPage.trigger('progress:set');
      });

      this.on('page:progress:saved', function () {
        if (EF.modules.every(module => module.get('completed') || module.get('skip_activity_progress'))) {
          const data = {
            event_type: 'content-complete',
          };
          window.postMessage(data, window.location.origin);
        }
      });
    },

    // ### EF.registerPage
    //
    // Takes a page key and a EF.Models.ContentPage object and an optional options
    // object. current the options object can have a 'force' key..
    // This is how you register a ContentPage with the API.
    // Returns boolean of whether or not the page was registered.
    // EF.registerPage('page1', contentPage);
    //
    // Registering a page that is already present will fail, unless you include
    // force: true in the options object
    registerPage: function (pageKey, contentPage, options) {
      options = options || {};
      var activityKey, moduleKey;
      try {
        activityKey = contentPage.get('activityKey') || options.activityKey || this.module.currentActivity.get('key');
        moduleKey = contentPage.get('moduleKey') || options.moduleKey || this.module.get('key');
      } catch (e) {
        if (!moduleKey || !activityKey) {
          throw new Error(
            'Must specify moduleKey and activityKey when registering your content pages. Like so:\n' +
              "EF.registerPage('page_1', page, {moduleKey: 'module_1', activityKey: 'activity_1'});\n " +
              'or set the module and activity before registering your pages: \n' +
              "EF.module = module; module.setActivityByKey('activity_1');"
          );
        }
      }

      this.registeredPages[moduleKey] = this.registeredPages[moduleKey] || {};
      this.registeredPages[moduleKey][activityKey] = this.registeredPages[moduleKey][activityKey] || {};

      if (!this.registeredPages[moduleKey][activityKey].hasOwnProperty(pageKey) || options.force === true) {
        this.registeredPages[moduleKey][activityKey][pageKey] = contentPage;
        return true;
      }
      return false;
    },

    // ### EF.registerPage
    //
    // Takes a EF.Models.ContentPage object, a page key, and an activity key.
    // Method loops through list of modules which have an activity matching the key provided.
    // For those found, the ContentPage will be registered under the provided page key,
    // activity key, and the found module.  This can be used when a page repeats
    // across multiple modules.
    //
    registerPageToMultipleModules: function (page, pageKey, activityKey) {
      for (var i = 0; i < EF.modules.models.length; i++) {
        var module = EF.modules.models[i];
        if (module.activities.getActivityByyKey(activityKey)) {
          var keys = { moduleKey: EF.modules.models[i].get('key'), activityKey: activityKey, pageKey: pageKey };
          EF.registerPage(pageKey, page, keys);
        }
      }
    },

    // ### EF.isPageRegistered
    //
    // isPageRegistered takes a pageKey, activityKey and moduleKey
    // and returns true or false depending on if there is a
    // page with that set of keys yet.
    //
    isPageRegistered: function (pageKey, activityKey, moduleKey) {
      try {
        return (
          this.registeredPages[moduleKey].hasOwnProperty(activityKey) &&
          this.registeredPages[moduleKey][activityKey].hasOwnProperty(pageKey)
        );
      } catch (e) {
        return false;
      }
    },

    listen: function (message) {
      if (window.postMessageTransporter) {
        window.postMessageTransporter.addListener(message);
      }
    },

    receiveMessage: function (data) {
      if (!data.sbMessage) return false;

      if (data.sbMessage == 'sbDynamicHeightOn') {
        EF.sandboxHelper.postDynamicHeight();
      }
    },

    getEnrollment: function () {
      var attrs = ['id', 'userId', 'name', 'schoolName', 'className'];
      return new EF.Models.Enrollment(_.pick(this.urlParams, attrs));
    },

    sortModulesBy: function (attr) {
      return this.modules.sortBy(function (m) {
        return m.get(attr);
      });
    },

    unlockModules: function () {
      this.modules.each(function (module) {
        module.set('unLocked', false);
        module.set('group_locked', true);
      });

      return _.map(this.currentGroup, function (module) {
        module.set('unLocked', true);
        module.set('group_locked', false);
        return module;
      });
    },

    unlockedModules: function () {
      this.unlockModules();
      return this.modules.filter(function (module) {
        return module.get('unLocked');
      });
    },

    isUnlockedByKey: function (moduleKey) {
      var module = _.find(this.unlockedModules, function (mod) {
        return mod.get('key') == moduleKey;
      });
      return module && module.length;
    },

    getModuleByFilter: function (q, filter) {
      return this.modules.find(function (mod) {
        return mod.get(filter) === q;
      });
    },

    //returns a module by moduleKey but does not set EF.module
    getModuleByKey: function (moduleKey) {
      return this.getModuleByFilter(moduleKey, 'key');
    },

    getModuleByIndex: function (moduleIndex) {
      return this.getModuleByFilter(moduleIndex, 'index');
    },

    //returns and sets EF.module by moduleKey
    setModuleByKey: function (moduleKey) {
      this.module = this.modules.getModuleByKey(moduleKey);
      return this.module;
    },

    setModules: function (modules) {
      const options = {
        parse: true,
      };
      this.modules = new EF.Collections.ModulesCollection(modules, options);
    },

    setGroups: function (groups) {
      this.groups = new EF.Collections.GroupCollection(groups);
    },

    setModulesByJSON: function (modulesJSON, modulesContentJson, callback) {
      var self = this;
      // var moduleObjects = _.map(modulesJSON, function(mod, i) {
      //   if (!mod.index) { mod.index = i; }
      //   return new EF.Models.Module(mod);
      // });

      // Initialize an empty modules collection. This collection
      // will now be responsible for loading the progress.
      this.setModules(null);

      // load customizations first to get injected or replacement pages
      EF.ContentProvider.CustomizationService.load().then(() => {
        self.ContentProvider.InitializationService.applyTransformations(
          modulesJSON,
          modulesContentJson,
          this.groupsJSON
        ).then(([modulesJSON, modulesContentJson, groupsJSON]) => {
          this.modulesJSON = modulesJSON;
          this.groupsJSON = groupsJSON;

          // Store the module JSON on the collection, will be used in the parse
          // method to translate module_statuses to page level progress.
          this.modules.content_modules = modulesJSON;

          this.modules.fetch({
            success: callback,
            error: this.loadError,
          });
        });
      });
      // this.loadModulesProgress(callback);
    },

    setGroupsByJSON: function (groupsJSON) {
      var groupObjects = _.map(groupsJSON, function (group, i) {
        return new EF.Models.Group(group);
      });
      this.setGroups(groupObjects);
    },

    // ### EF.registerPageLoader
    //
    // method to assigning a page loading method for the framework to use to find
    // the page objects.
    //
    registerPageLoader: function (pageLoader) {
      this.pageLoader = pageLoader;
    },

    // ### loadPage
    //
    // takes one argument: a page key and loads a specific page using the
    // Page Loader registered by your application.

    loadPage: function (contentPageKey, callback) {
      if (typeof this.pageLoader === 'undefined') {
        var message = 'You must defined a page loading function with EF.registerPageLoader';
        var err = new Error(message);
        throw err;
      }
      var self = this;

      var pageLoaded = function (page) {
        // If the pages is not loaded it will trigger 'page_not_loaded' on
        // the EF object passing the pageKey as an argument.
        if (!page) {
          EF.trigger('contentPage:notLoaded', contentPageKey);
          if (callback) callback();
          return;
        }
        EF.currentPage = page;
        EF.module.currentActivity.currentPage = EF.module.currentActivity.pages.getPageByKey(page.key);

        page.moduleId = EF.module.get('id');
        page.moduleKey = EF.module.get('key');
        page.activityKey = EF.module.currentActivity.get('key');
        EF.trigger('contentPage:loaded', page);

        page.setProgress(self.module.progress.forPageAndActivity(contentPageKey, page.activityKey));

        if (EF.stage) EF.stage.empty();
        page.stage = EF.stage;

        function initCallback() {
          page.play();
          if (callback) callback(page);
          page.unbind('complete');
          page.once('complete', function pageComplete() {
            EF._markPageComplete(contentPageKey, page);
            page.unload();
          });
        }

        if (page.init.length == 1) {
          page.init(initCallback);
        } else {
          page.init();
          return initCallback();
        }
      };

      var page = this.pageLoader.apply(this, [contentPageKey, pageLoaded]);
    },

    reloadCurrentPage: function () {
      if (EF.currentPage) {
        EF.loadPage(EF.currentPage.key);
      }
    },

    // The default page loader pulls pages off registeredPages object
    //
    _defaultPageLoader: function (pageKey, callback) {
      var page;
      try {
        var modPages = EF.registeredPages[this.module.get('key')];
        if (modPages) {
          var actPages = modPages[this.module.currentActivity.get('key')];
          page = actPages[pageKey];
          if (page.multiplePageKeys) {
            page.key = pageKey;
            page.pageKey = pageKey;
          }
        }
        if (callback) callback(page);
        return page;
      } catch (e) {
        console.log(e);
        console.log(e.stack);
        if (callback) callback();
        return;
      }
    },

    // ### onDemandPageLoader
    // Page loader which uses `require.js` to load in a script file for the page that is about to be loaded.
    // This script is expected to register the page in registeredPages as this then calls the defaultPageLoader
    //
    onDemandPageLoader: function (pageKey, callback) {
      if (typeof require === 'function') {
        var defaultCallback = function () {
          EF._defaultPageLoader(pageKey, callback);
        };

        var requirePageFn = function () {
          require([EF.services.assets.pageScriptFile(pageKey)], defaultCallback);
        };

        var requireActivityFn = function () {
          require([EF.services.assets.pageScriptFile(EF.module.currentActivity.get('key'))], requirePageFn);
        };

        var requireModuleFn = function () {
          var callback = requirePageFn;
          if (EF.module.currentActivity.get('has_common_script')) {
            callback = requireActivityFn;
          }
          require([EF.services.assets.scriptFile(EF.module.get('key'), EF.module.get('key'))], callback);
        };

        if (EF.module.get('has_common_script')) {
          requireModuleFn();
        } else if (EF.module.currentActivity.get('has_common_script')) {
          requireActivityFn();
        } else {
          requirePageFn();
        }
      } else {
        var message = 'To use the onDemandPageLoader you must include require.js.';
        var err = new Error(message);
        throw err;
      }
    },

    // ### initPage
    // Loads page progress, sets it to current page, triggers events.
    // To be used when not having the framework control the flow of th application
    // in terms of loading/preloading/cleaning up pages.
    //
    // takes: a page key
    // returns: page;
    //
    initPage: function (pageKey, callback) {
      var page = this._defaultPageLoader.apply(this, [pageKey]);
      if (!page) {
        var ak = EF.module.currentActivity ? EF.module.currentActivity.get('key') : undefined;
        var e = new Error(
          'Page Key is not registered. module:' + EF.module.get('key') + ', activity: ' + ak + 'page key:' + pageKey
        );
        throw e;
      }
      EF.currentPage = page;
      EF.module.currentActivity.currentPage = EF.module.currentActivity.pages.getPageByKey(page.key);
      page.init();
      page.stage = EF.stage;

      page.moduleId = EF.module.get('id');
      page.moduleKey = EF.module.get('key');
      page.activityKey = EF.module.currentActivity.get('key');
      page.key = pageKey;

      EF.trigger('contentPage:loaded', page);

      if (page.key === EF.module.getFirstPage().get('key')) {
        EF.trigger('module:start', page);
      }

      if (page.key === EF.module.getFirstActivity().getFirstPage().get('key')) {
        EF.trigger('activity:start', page);
      }

      page.setProgress(this.module.progress.forPageAndActivity(pageKey, page.activityKey));
      page.trigger('page:progress:load');

      page.unbind('complete');
      page.once('complete', function pageComplete() {
        EF._markPageComplete(pageKey, page);
        page.unload();
      });

      if (callback) callback(page);
      return page;
    },

    // ### EF.start
    //
    // this function signals the API to start the application and calls the
    // loadModule function with the module
    start: function () {
      this.loadModule();
    },

    // loads the 'startingPage' of the module. the starting page
    // is based on the farthest progress of the user. Play is called
    // on the starting page. Takes a callback
    //
    // If the module is completed it will not load a page, but it will trigger
    // 'nopage' on the EF object.
    //
    loadModule: function (callback, skipLoadPage) {
      var self = this;
      this.module.loadProgress(function () {
        EF.trigger('module:loaded', self.module);
        EF.module.getStartingActivity();
        if (!EF.module.currentActivity) {
          // couldn't find a starting activity, must be complete
          // start at the beginning to let the user retake it
          EF.module.currentActivity = EF.module.activities.head();
          if (EF.module.currentActivity.isComplete()) {
            EF.module.currentActivity.set('redo', true);
          }
        }
        if (skipLoadPage) {
          if (callback) callback();
        } else {
          var page = self.module.getStartingPage();
          if (page) {
            var key = page.get('key');
            self.loadPage(key, callback);
          } else {
            self.trigger('nopage');
          }
        }
      });
    },

    // Loads module progress for the user, finds the starting page,
    // and inits the page. Method takes a callback.
    //
    // If the module is completed it will not init a page, but it will trigger
    // 'nopage' on the EF object.
    //
    // This method sets 'EF.startingPageKey' which can be used to manually
    // init the page once you are ready to start it and it has been registered
    // with the framework with EF.registerPage.
    //
    // once you are ready to go with a page, init it by calling
    // EF.initPage(pageKey, callback);

    // Calls the callback passed in with true if there is a page,
    // calls it with false if there is none.
    initModule: function (callback) {
      EF.module.loadProgress(function () {
        EF.trigger('module:load', EF.module);
        EF.module.getStartingActivity();
        EF.startingPage = EF.module.getStartingPage();
        if (EF.startingPage) {
          var key = EF.startingPage.get('key');
          EF.startingPageKey = key;

          if (callback) callback(true);
        } else {
          EF.trigger('nopage');
          if (callback) callback(false);
        }
      });
    },

    loadModulesProgress: function (callback) {
      var self = this;
      if (!this.modules) return;
      var mod_keys = this.modules.map(function (mod) {
        return mod.get('key');
      });
      if (this.options.local) {
        this._loadModulesProgressLocal(mod_keys, callback);
      } else {
        this._loadModulesProgressRemote(mod_keys, callback);
      }
    },

    _loadModulesProgressRemote: function (mod_keys, callback) {
      var self = this;
      var eid = EF.enrollment.get('id');
      var url = EF.url('/sdk/enrollments/' + eid + '/page_progresses.json');
      var corsHackApplied;

      var params = { module_keys: mod_keys };

      var xhr = $.ajax({
        dataType: $.support.cors ? 'json' : 'jsonp',
        url: url,
        data: params,
        crossDomain: true,
        xhrFields: {
          withCredentials: true,
        },
        success: function (data, textStatus, jqXHR) {
          self.modules.each(function (module) {
            var modData;
            if (data) {
              modData = data[module.get('key')];
              module.progress._saveLocal(modData, function () {});
            } else {
              modData = {};
            }
            module._setProgressOnPages(modData);
            module.getStartingActivity();
            EF.startingPage = module.getStartingPage();
            module.set('progressLoaded', true);
          });
          EF.trigger('modulesProgress:loaded');
          callback();
        },
      });

      xhr.fail(function (jqXHR, textStatus, errorThrown) {
        if (corsHackApplied) {
          alert('Error loading your information');
          return;
        }
        corsHackApplied = true;
        $.support.cors = false;
        self._loadModulesProgressRemote(mod_keys, callback);
      });
    },

    _loadModulesProgressLocal: function (mod_keys, callback) {
      var l = this.modules.length,
        i = 0,
        self = this;
      var checkCount = function () {
        i++;
        if (i === l) {
          EF.trigger('modulesProgress:loaded');
          callback();
        }
      };
      setTimeout(function () {
        self.modules.each(function (mod) {
          mod.loadProgress(checkCount);
        });
      }, 0);
    },
    // ### EF.loadActivity
    //
    // loads and plays a certain activity.
    loadActivity: function (activityKey, callback, skipLoadPage) {
      var activity = EF.module.loadActivity(activityKey);
      function startPage() {
        if (activity.isComplete()) {
          activity.set('redo', true);
        }
        if (skipLoadPage) {
          if (callback) {
            callback();
          }
        } else {
          var page = EF.module.getStartingPage();
          if (page) {
            var key = page.get('key');
            EF.loadPage(key, callback);
            activity.moduleKey = EF.module.get('key');
            EF.trigger('activity:load', activity);
          } else {
            EF.trigger('nopage');
            if (callback) callback(false);
          }
        }
      }

      if (EF.module.get('progressLoaded')) {
        startPage();
      } else {
        EF.module.loadProgress(startPage);
      }
    },

    //
    // ### _markPageComplete
    //
    // saves the progress of the page, triggers page:complete event and loads
    // the next page via the EF.loadNextPage method.
    //
    _markPageComplete: function (key, contentPage) {
      contentPage.completed = true;
      contentPage.set('completed', true);
      this.module.saveProgress(contentPage, function () {
        EF.trigger('contentPage:complete', contentPage);
        if (!EF.options.apiOnly) {
          EF.loadNextPage();
        }
      });
    },

    // ### EF.loadNextPage
    //
    // Will get the next page in the module and call initPage or loadPage
    // with it based on if apiOnly is set.
    //
    loadNextPage: function () {
      var nextPage = this.module.getNextPage();
      if (nextPage) {
        if (EF.options.apiOnly) {
          this.initPage(nextPage.get('key'));
        } else if (EF.router) {
          EF.router.navigate(
            nextPage.get('moduleKey') + '/' + nextPage.get('activityKey') + '/' + nextPage.get('key'),
            { trigger: true }
          );
        } else {
          this.loadPage(nextPage.get('key'));
        }
      } else {
        // When the module is completed it will fire a 'module:complete' event
        // on the EF object and 'complete' on the module itself
        EF.trigger('module:complete', EF.module);
        EF.module.trigger('complete');
      }
    },

    // ### EF.getNextPageByKey
    //
    // takes a page key and returns the next pageKey in the sequence.
    // if the next page doesn't exist, for example if this page is the last
    // in an activity it returns undefined.
    getNextPageByKey: function (pageKey) {
      var page = this.module.getNextPageByKey(pageKey);
      if (page) return page.get('key');
    },

    // ### EF.getMostRecentPage
    //
    // Retrieves the most recent page that the user was on. Takes a
    // callback and calls it with an object that has keys for the information
    // about the last page, including pageKey, activityKey, moduleKey, moduleId
    getMostRecentPage: function (callback) {
      this.module.progress.getMostRecentPage(callback);
    },

    _getSandboxHelperObj: function () {
      if (!this.options.sandboxMode) return false;
      return new window.SandboxHelper();
    },

    turnOnSandboxMode: function () {
      this.options.sandboxMode = true;
      this.sandboxHelperObj = this._getSandboxHelperObj();
      this.sandboxHelperObj.postEnrollment();
      this.sandboxHelperObj.postModules();
      console.log('sandbox mode is on');
    },

    turnOffSandboxMode: function () {
      this.options.sandboxMode = false;
      this.sandboxHelperObj = undefined;
      console.log('sandbox mode is off');
    },

    _getContentPageObj: function (moduleKey, activityKey, pageKey) {
      var getGenericContentPage = function () {
        var contentPage = new EF.Models.ContentPage({
          moduleKey: moduleKey,
          activityKey: activityKey,
          pageKey: pageKey,
          key: pageKey,
          enrollmentId: EF.enrollment.get('id'),
        });
        return contentPage;
      };

      var getRegisteredContentPage = function () {
        // hack around the use of this to get the module right
        var mod = new EF.Models.Module({
          key: moduleKey,
        });
        mod.currentActivity = new EF.Models.Activity({});
        var scope = {
          module: mod,
        };
        var contentPage = EF.pageLoader.apply(scope, [pageKey]);

        if (contentPage) {
          contentPage.set({
            moduleKey: moduleKey,
            activityKey: activityKey,
            pageKey: pageKey,
            key: pageKey,
            enrollmentId: EF.enrollment.get('id'),
          });
        }
        return contentPage;
      };

      var page = getRegisteredContentPage();

      if (typeof page === 'undefined') {
        page = getGenericContentPage();
      }
      return page;
    },

    // ### EF.getContentPage
    //
    // This method takes a moduleKey, activityKey, pageKey and a callback.
    // Calls the passed callback with the content page object
    // with its progress loaded.  This content page object is a standalone.
    // It is simply a ContenPage object without any logic.
    getContentPage: function (moduleKey, activityKey, pageKey, callback) {
      var func = EF.options.local ? this._getContentPageLocal : this._getContentPageRemote;
      var args = _.toArray(arguments);
      var contentPage = this._getContentPageObj.apply(this, arguments);
      args.push(contentPage);
      func.apply(this, args);
    },

    _getContentPageLocal: function (moduleKey, activityKey, pageKey, callback, contentPage) {
      // select progress out of localStorage based on moduleKey
      var progStr = window.LocalStorageWrapper.getItem('progress:' + moduleKey);
      var prog = progStr ? JSON.parse(progStr) : {};
      var pageProg = prog[activityKey] ? prog[activityKey][pageKey] : false;
      contentPage.setProgress(pageProg);

      callback(contentPage);
    },

    _getContentPageRemote: function (moduleKey, activityKey, pageKey, callback, contentPage) {
      var url = EF.url('/sdk/enrollments/' + EF.enrollment.get('id') + '/page_progresses.json?'),
        self = this,
        xhr,
        params = {
          moduleKey: moduleKey,
          activityKey: activityKey,
          pageKey: pageKey,
          key: pageKey,
        };
      xhr = $.ajax({
        url: url,
        data: params,
        dataType: 'jsonp',
        success: function (single_page_progress) {
          contentPage.setProgress(single_page_progress);
          callback(contentPage);
        },
      });
    },

    // ### EF.cleaLocalProgress
    //
    // Clear the progress stored in local storage for the module. Should need
    // only be used in development mode.
    clearLocalProgress: function () {
      window.LocalStorageWrapper.clear();
    },

    url: function (path) {
      return EF.config.get('basePath') + path;
    },

    surveyUrl: function (path) {
      return EF.config.get('assessmentEndpoint') + path;
    },

    // ### EF.registerToolbar
    //
    // This function is used to register a toolbar element on the page.
    // takes a jQuery object or a selector for the toolbar on the page.
    // This function will hookup events to call play, paise, stop, mute, reload
    // methods on the current page.
    registerToolbar: function (toolbar) {
      this.toolbar = $(toolbar);
      this.toolbar.on('click', '[data-play]', function () {
        EF.currentPage.play();
        EF.currentPage.trigger('play');
      });
      this.toolbar.on('click', '[data-pause]', function () {
        EF.currentPage.pause();
        EF.currentPage.trigger('pause');
      });
      this.toolbar.on('click', '[data-stop]', function () {
        EF.currentPage.stop();
        EF.currentPage.trigger('stop');
      });
      this.toolbar.on('click', '[data-mute]', function () {
        EF.currentPage.mute();
        EF.currentPage.trigger('pause');
      });
      this.toolbar.on('click', '[data-reload]', function () {
        EF.currentPage.reload();
        EF.currentPage.trigger('reload');
      });
      this.toolbar.on('click', '[data-forward]', function () {
        EF.currentPage.forward();
        EF.currentPage.trigger('forward');
      });
      this.toolbar.on('click', '[data-back]', function () {
        EF.currentPage.back();
        EF.currentPage.trigger('back');
      });
      this.toolbar.on('click', '[data-unpause]', function () {
        EF.currentPage.unpause();
        EF.currentPage.trigger('unpause');
      });
      this.toolbar.on('click', '[data-home]', function () {
        EF.currentPage.unload();
        EF.currentPage.trigger('unload');
        //TODO: fix this! need to have a way of managing where the home button goes
        EF.router.navigate('app_home/index/menu', { trigger: true });
      });
    },

    // Change the module. Should call EF.initModule or EF.loadModule after
    // switching the module.
    changeModule: function (module) {
      this.module = module;
    },

    JSON: {
      parse: function (str) {
        if (typeof str === 'undefined') {
          return str;
        }
        try {
          return JSON.parse(str);
        } catch (e) {
          return str;
        }
      },
      stringify: function (value) {
        // JSON.stringify('off') yields "off" with quotes around it.
        // Leave the value alone if it is a primitive
        return Object(value) === value ? JSON.stringify(value) : value;
      },
    },
  });

  // Can set config attributes but can't change them
  // ## EF.Config
  //
  // This is an object for setting and retrieving configuration options for a
  // use within the API. For example, set the survey endpoint to be used.
  var configAttributes;
  EF.Config = function (config) {
    var defaultAttributes = {
      basePath: 'https://api.everfi.net',
      assessmentEndpoint: 'https://surveys.everfi.net',
      webChatBasePath: 'https://apps.mypurecloud.com/widgets/9.0',
    };
    configAttributes = _.defaults(config, defaultAttributes);
  };

  EF.Config.prototype = {
    // retrieve a config value by key
    get: function (key) {
      return configAttributes[key];
    },

    getAll: function () {
      return configAttributes;
    },
  };
})(window.EF);
